/**
 * ============================================
 * USE ENVIRONMENTS HOOK
 * ============================================
 * 
 * Fetches the list of environments from backend.
 */

import { useState, useEffect, useCallback } from 'react';
import { getEnvironments, type EnvironmentSummary } from '@/services/api';
import type { EnvironmentConfig } from '@/config';

interface UseEnvironmentsResult {
  environments: EnvironmentConfig[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

function mapToEnvironmentConfig(env: EnvironmentSummary): EnvironmentConfig {
  return {
    id: env.id,
    name: env.name,
    shortName: env.short_name,
    color: env.color,
    isActive: env.is_active,
  };
}

export function useEnvironments(): UseEnvironmentsResult {
  const [environments, setEnvironments] = useState<EnvironmentConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEnvironments = useCallback(async () => {
    try {
      const result = await getEnvironments();
      const mapped = result.environments
        .filter(e => e.is_active)
        .map(mapToEnvironmentConfig);
      setEnvironments(mapped);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch environments:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnvironments();
  }, [fetchEnvironments]);

  return {
    environments,
    isLoading,
    error,
    refetch: fetchEnvironments,
  };
}
